'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Edit as EditIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import STLViewer from 'components/viewer/STLViewer';

interface Asset {
  id: string;
  name: string;
  url: string;
  type: string;
}

interface AssetGalleryModalProps {
  open: boolean;
  onClose: () => void;
  assets: Asset[];
  initialAssetIndex: number;
  isOwnProfile: boolean;
  onRename: (assetId: string, newName: string) => Promise<void>;
}

export default function AssetGalleryModal({
  open,
  onClose,
  assets,
  initialAssetIndex,
  isOwnProfile,
  onRename,
}: AssetGalleryModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialAssetIndex);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');

  const currentAsset = assets[currentIndex];

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % assets.length);
    setIsEditing(false);
  };

  const handlePrev = () => {
    setCurrentIndex(prev => (prev - 1 + assets.length) % assets.length);
    setIsEditing(false);
  };

  const handleStartEditing = () => {
    setNewName(currentAsset.name);
    setIsEditing(true);
  };

  const handleSaveRename = async () => {
    if (newName.trim() && newName !== currentAsset.name) {
      await onRename(currentAsset.id, newName.trim());
    }
    setIsEditing(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ position: 'relative', p: 0, bgcolor: 'background.default' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'background.paper' }}
        >
          <CloseIcon />
        </IconButton>

        {assets.length > 1 && (
          <>
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'background.paper',
              }}
            >
              <PrevIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'background.paper',
              }}
            >
              <NextIcon />
            </IconButton>
          </>
        )}

        <Box
          sx={{
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {currentAsset.type === 'STL' ? (
              <STLViewer url={currentAsset.url} />
            ) : (
              <Box
                component="img"
                src={currentAsset.url}
                alt={currentAsset.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
                {...({} as React.ComponentProps<'img'>)}
              />
            )}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            {isEditing ? (
              <>
                <TextField
                  size="small"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  autoFocus
                  sx={{ minWidth: 200 }}
                />
                <IconButton size="small" onClick={handleSaveRename} color="primary">
                  <CheckIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography variant="subtitle1">{currentAsset.name}</Typography>
                {isOwnProfile && (
                  <IconButton size="small" onClick={handleStartEditing}>
                    <EditIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
